<script setup>
import { ref, computed, onMounted, onBeforeUnmount, watch, nextTick } from 'vue'
import Chart from 'chart.js/auto'

const props = defineProps({
  review: {
    type: Object,
    required: true,
  },
  threshold: {
    type: Number,
    default: 3.5,
  },
  jitter: {
    type: Number,
    default: 0.35,
  },
  seed: {
    type: Number,
    default: 42,
  },
  highlightedId: {
    type: [Number, String, null],
    default: null,
  },
})

const emit = defineEmits(['pointClick', 'chartClick'])

// --- Quadrant colors & labels ---
const quadrantMeta = {
  quickWins: { label: 'Schnelle Erfolge', color: '#2e7d32' },
  majorProjects: { label: 'Strategische Projekte', color: '#1565c0' },
  fillers: { label: 'Kleine Extras', color: '#6d4c41' },
  avoid: { label: 'Energieverschwendung', color: '#c62828' },
}

const RING_STEP = 0.18 // ring radius in coordinate units
const LABEL_PADDING = 18 // px distance of quadrant labels from chart edge

// --- Spread points at the same coordinate in concentric rings ---
const scatterData = computed(() => {
  const STEP = RING_STEP

  // 1. Build raw list
  const raw = []
  for (const [key, actions] of Object.entries(props.review.quadrants)) {
    if (!actions?.length) continue
    const meta = quadrantMeta[key] || { label: key, color: '#999' }
    for (const action of actions) {
      raw.push({
        effort: action.effort,
        impact: action.impact,

        title: action.customDefinition || action.title,
        key: action.key,
        id: action.id,
        quadrant: meta.label,
        quadrantKey: key,
        areaColor: action.areaColor || '#1D7CB8',
      })
    }
  }

  // 2. Group by integer coordinate
  const groups = {}
  for (const p of raw) {
    const k = `${p.effort},${p.impact}`
    ;(groups[k] ??= []).push(p)
  }

  // 3. Arrange each group in concentric rings, clamped to chart bounds only
  const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v))
  const points = []
  for (const group of Object.values(groups)) {
    const { effort, impact } = group[0]

    let idx = 0
    let ring = 0
    while (idx < group.length) {
      if (ring === 0) {
        group[idx].x = effort
        group[idx].y = impact
        idx++
      } else {
        const ringCount = ring * 6
        const radius = ring * STEP
        for (let i = 0; i < ringCount && idx < group.length; i++, idx++) {
          const ringOffset =
            (ring % 2 === 0 ? 0 : Math.PI / ringCount) + Math.PI / 6
          const angle = (2 * Math.PI * i) / ringCount + ringOffset
          group[idx].x = clamp(effort + Math.cos(angle) * radius, 0.6, 6.4)
          group[idx].y = clamp(impact + Math.sin(angle) * radius, 0.6, 6.4)
        }
      }
      ring++
    }
    points.push(...group)
  }
  return points
})

// --- Cluster background plugin ---
const clusterPlugin = {
  id: 'clusterBackground',
  beforeDatasetsDraw(chart) {
    const { ctx, scales } = chart
    if (!scales.x || !scales.y) return

    // Group points by original coordinate
    const groups = {}
    for (const p of scatterData.value) {
      const k = `${p.effort},${p.impact}`
      ;(groups[k] ??= []).push(p)
    }

    ctx.save()
    for (const group of Object.values(groups)) {
      if (group.length <= 1) continue

      const { effort, impact } = group[0]
      const rings = Math.ceil((group.length - 1) / 6)
      const px = scales.x.getPixelForValue(effort)
      const py = scales.y.getPixelForValue(impact)
      // Convert RING_STEP coord units to pixels
      const pxPerUnit = Math.abs(
        scales.x.getPixelForValue(1) - scales.x.getPixelForValue(0),
      )
      const r = (rings * RING_STEP + 0.14) * pxPerUnit

      ctx.beginPath()
      ctx.arc(px, py, r, 0, Math.PI * 2)
      ctx.fillStyle = 'rgba(68, 91, 106, 0.05)'
      ctx.fill()
    }
    ctx.restore()
  },
}

// --- Quadrant background plugin ---
const quadrantLineOpacity = () => {
  const n = scatterData.value.length
  const delay = (n - 1) * 18 * 0.75
  const elapsed = Date.now() - chartStartTime
  return Math.min(1, Math.max(0, (elapsed - delay) / 500))
}

const quadrantPlugin = {
  id: 'quadrantBackground',
  beforeDatasetsDraw(chart) {
    const { ctx, chartArea, scales } = chart
    if (!chartArea) return

    const opacity = quadrantLineOpacity()

    if (opacity < 1 && !quadrantAnimFrame) {
      quadrantAnimFrame = requestAnimationFrame(() => {
        quadrantAnimFrame = null
        chartInstance?.render()
      })
    }

    if (opacity === 0) return

    const threshold = chart.options.plugins.quadrantBackground?.threshold ?? 3
    const xT = scales.x.getPixelForValue(threshold)
    const yT = scales.y.getPixelForValue(threshold)
    const { left, right, top, bottom } = chartArea

    ctx.save()
    ctx.globalAlpha = opacity
    ctx.strokeStyle = 'rgba(68, 91, 106, 0.5)'
    ctx.lineWidth = Math.min(2, Math.round(chart.width * 0.015))
    ctx.beginPath()
    ctx.moveTo(xT, top)
    ctx.lineTo(xT, bottom)
    ctx.stroke()
    ctx.beginPath()
    ctx.moveTo(left, yT)
    ctx.lineTo(right, yT)
    ctx.stroke()
    ctx.restore()
  },
  afterDraw(chart) {
    const { ctx, chartArea, scales } = chart
    if (!chartArea) return

    const opacity = quadrantLineOpacity()
    if (opacity === 0) return

    const { left, right, top, bottom } = chartArea
    const fontSize = Math.min(15, Math.round(chart.width * 0.022))
    const axisFontSize = Math.min(13, Math.round(chart.width * 0.016))
    const AXIS_PADDING = 9 // px gap between chart edge and axis numbers

    ctx.save()
    ctx.globalAlpha = opacity
    ctx.font = `bold ${fontSize}px Frutiger, Arial, sans-serif`

    const hPad = 7
    const vPad = 4
    const quadrantLabels = [
      {
        text: 'Aufwändige Schlüsselmaßnahmen',
        x: left + LABEL_PADDING,
        y: top + fontSize + LABEL_PADDING,
        align: 'left',
        color: 'rgba(68, 91, 106, 1)',
      },
      {
        text: 'Hebel für schnelle Inklusionsgewinne',
        x: right - LABEL_PADDING,
        y: top + fontSize + LABEL_PADDING,
        align: 'right',
        color: 'rgba(68, 91, 106, 1)',
      },
      {
        text: 'Wirkungssschwache Ressourcenfresser',
        x: left + LABEL_PADDING,
        y: bottom - LABEL_PADDING,
        align: 'left',
        color: 'rgba(68, 91, 106, 1)',
      },
      {
        text: 'Unterstützende kleine Aktivitäten',
        x: right - LABEL_PADDING,
        y: bottom - LABEL_PADDING,
        align: 'right',
        color: 'rgba(68, 91, 106, 1)',
      },
    ]
    for (const { text, x, y, align, color } of quadrantLabels) {
      const tw = ctx.measureText(text).width
      const rx = align === 'left' ? x - hPad : x - tw - hPad
      const ry = y - fontSize - vPad
      const rw = tw + 2 * hPad
      const rh = fontSize + Math.round(fontSize * 0.3) + 2 * vPad
      ctx.fillStyle = color
      ctx.beginPath()
      if (ctx.roundRect) ctx.roundRect(rx, ry, rw, rh, 3)
      else ctx.rect(rx, ry, rw, rh)
      ctx.fill()
      ctx.fillStyle = '#fff'
      ctx.textAlign = align
      ctx.fillText(text, x, y)
    }

    // Axis numbers 1–6, centered in each cell
    ctx.font = `${axisFontSize}px Frutiger, Arial, sans-serif`
    ctx.fillStyle = 'rgba(68, 91, 106, 0.6)'
    ctx.textBaseline = 'top'
    ctx.textAlign = 'center'
    for (let n = 1; n <= 6; n++) {
      const xPx = scales.x.getPixelForValue(n)
      ctx.fillText(String(n), xPx, bottom + AXIS_PADDING)
    }
    ctx.textBaseline = 'middle'
    ctx.textAlign = 'right'
    for (let n = 1; n <= 6; n++) {
      const yPx = scales.y.getPixelForValue(n)
      ctx.fillText(String(n), left - AXIS_PADDING, yPx)
    }

    // Axis labels + range hints
    const axisLabelFontSize = Math.min(16, Math.round(chart.width * 0.024))
    const scaleHintFont = `${Math.round(axisFontSize * 0.85)}px Frutiger, Arial, sans-serif`
    const scaleHintColor = 'rgba(68, 91, 106, 0.8)'

    // AUFWAND: centered below numbers row
    const numbersRowY = bottom + AXIS_PADDING
    ctx.font = `bold ${axisLabelFontSize}px Frutiger, Arial, sans-serif`
    ctx.fillStyle = '#445B6A'
    ctx.textBaseline = 'top'
    ctx.textAlign = 'center'
    ctx.fillText('AUFWAND', (left + right) / 2, numbersRowY + axisFontSize + 4)
    ctx.font = scaleHintFont
    ctx.fillStyle = scaleHintColor
    ctx.textAlign = 'left'
    ctx.fillText('sehr hoch', left, numbersRowY + axisFontSize + 4)
    ctx.textAlign = 'right'
    ctx.fillText('sehr gering', right, numbersRowY + axisFontSize + 4)

    // IMPACT: rotated, centered on left
    ctx.save()
    ctx.translate(left - AXIS_PADDING - axisFontSize - 4, (top + bottom) / 2)
    ctx.rotate(-Math.PI / 2)
    ctx.font = `bold ${axisLabelFontSize}px Frutiger, Arial, sans-serif`
    ctx.fillStyle = '#445B6A'
    ctx.textBaseline = 'bottom'
    ctx.textAlign = 'center'
    ctx.fillText('IMPACT', 0, 0)
    ctx.font = scaleHintFont
    ctx.fillStyle = scaleHintColor
    ctx.textAlign = 'left'
    ctx.fillText('sehr gering', -(bottom - top) / 2, 0)
    ctx.textAlign = 'right'
    ctx.fillText('sehr hoch', (bottom - top) / 2, 0)
    ctx.restore()

    ctx.restore()
  },
}

// --- Highlight plugin: dim others + ring around active (canvas-only, no re-layout) ---
const highlightRingPlugin = {
  id: 'highlightRing',
  afterDatasetsDraw(chart) {
    const activeId = chart.options.plugins.highlightRing?.activeId
    if (activeId == null) return

    const dataset = chart.data.datasets[0]
    const meta = chart.getDatasetMeta(0)
    const ctx = chart.ctx
    const activeIdx = dataset.data.findIndex((p) => p.id === activeId)

    ctx.save()

    // Dim all non-active points with a semi-transparent overlay
    for (let i = 0; i < meta.data.length; i++) {
      if (i === activeIdx) continue
      const el = meta.data[i]
      const { x, y } = el.getProps(['x', 'y'], true)
      ctx.beginPath()
      ctx.arc(x, y, pointRadius.value, 0, Math.PI * 2)
      ctx.fillStyle = 'rgba(255, 255, 255, 0.75)'
      ctx.fill()
    }

    // Draw ring around active point in the point's own color
    if (activeIdx >= 0) {
      const el = meta.data[activeIdx]
      const { x, y } = el.getProps(['x', 'y'], true)
      const pointColor = dataset.data[activeIdx]?.areaColor ?? '#1D7CB8'
      ctx.beginPath()
      ctx.arc(x, y, pointRadius.value + pointRadius.value / 2, 0, Math.PI * 2)
      ctx.strokeStyle = pointColor
      ;(ctx.lineWidth = pointRadius.value / 3), ctx.stroke()
    }

    ctx.restore()
  },
}

// --- External HTML tooltip ---
const tooltipRef = ref(null)

function externalTooltip({ chart, tooltip }) {
  const el = tooltipRef.value
  if (!el) return

  if (tooltip.opacity === 0) {
    el.style.opacity = '0'
    return
  }

  const point = tooltip.dataPoints?.[0]?.raw
  if (!point) return

  el.innerHTML = ''
  if (point.key) {
    const keyEl = document.createElement('span')
    keyEl.className = 'AssessmentChart-tooltipKey'
    keyEl.textContent = point.key + ' '
    el.appendChild(keyEl)
  }
  const titleEl = document.createElement('span')
  titleEl.textContent = point.title ?? ''
  el.appendChild(titleEl)

  const metaEl = document.createElement('div')
  metaEl.className = 'AssessmentChart-tooltipMeta'
  metaEl.innerHTML = `<span><strong>Aufwand:</strong> ${point.effort}/6</span><span><strong>Wirkung:</strong> ${point.impact}/6</span>`
  el.appendChild(metaEl)

  const placements = {
    quickWins: 'bottom-left',
    majorProjects: 'bottom-right',
    fillers: 'top-left',
    avoid: 'top-right',
  }
  el.dataset.placement = placements[point.quadrantKey] ?? 'top-left'

  const canvasRect = chart.canvas.getBoundingClientRect()
  const containerRect = el.parentElement.getBoundingClientRect()
  const left = canvasRect.left - containerRect.left + tooltip.caretX
  const top = canvasRect.top - containerRect.top + tooltip.caretY

  el.style.left = `${left}px`
  el.style.top = `${top}px`
  el.style.opacity = '1'
}

// --- Chart instance ---
const canvasRef = ref(null)
const containerWidth = ref(600)
let chartInstance = null
let resizeObserver = null
let chartStartTime = 0
let quadrantAnimFrame = null
let animResetTimeout = null

// Scale helpers – all derived from container width
const pointRadius = computed(() =>
  Math.min(10, Math.round(containerWidth.value * 0.01)),
)

function buildChart() {
  if (animResetTimeout) {
    clearTimeout(animResetTimeout)
    animResetTimeout = null
  }
  if (quadrantAnimFrame) {
    cancelAnimationFrame(quadrantAnimFrame)
    quadrantAnimFrame = null
  }
  if (chartInstance) {
    chartInstance.destroy()
    chartInstance = null
  }
  if (!canvasRef.value) return
  chartStartTime = Date.now()

  const data = scatterData.value
  const pr = pointRadius.value

  chartInstance = new Chart(canvasRef.value, {
    type: 'scatter',
    data: {
      datasets: [
        {
          label: 'Maßnahmen',
          data,
          pointRadius: pr,
          pointHoverRadius: pr + pr / 2,
          pointBackgroundColor: data.map((p) => p.areaColor),
          pointBorderColor: '#fff',
          pointBorderWidth: 0,
        },
      ],
    },
    options: {
      responsive: true,
      maintainAspectRatio: true,
      aspectRatio: 1,
      layout: {
        padding: { left: 40, bottom: 40 },
      },

      animations: {
        x: { duration: 0 },
        y: { duration: 0 },
        radius: {
          from: 0,
          duration: (ctx) => (ctx.mode === 'default' ? 350 : 0),
          easing: 'easeOutBack',
          delay: (ctx) =>
            ctx.type === 'data' && ctx.mode === 'default'
              ? ctx.dataIndex * 18
              : 0,
        },
      },

      scales: {
        x: {
          min: 0.5,
          max: 6.5,
          reverse: true,
          afterBuildTicks: (axis) => {
            axis.ticks = [0.5, 1.5, 2.5, 3.5, 4.5, 5.5, 6.5].map((v) => ({
              value: v,
            }))
          },
          ticks: { display: false },
          grid: {
            tickLength: 0,
            color: (ctx) =>
              ctx.tick.value === 0.5 || ctx.tick.value === 6.5
                ? 'transparent'
                : 'rgba(0, 0, 0, 0.1)',
          },
          title: { display: false },
        },
        y: {
          min: 0.5,
          max: 6.5,
          afterBuildTicks: (axis) => {
            axis.ticks = [0.5, 1.5, 2.5, 3.5, 4.5, 5.5, 6.5].map((v) => ({
              value: v,
            }))
          },
          ticks: { display: false },
          grid: {
            tickLength: 0,
            color: (ctx) =>
              ctx.tick.value === 0.5 || ctx.tick.value === 6.5
                ? 'transparent'
                : 'rgba(0, 0, 0, 0.1)',
          },
          title: { display: false },
        },
      },
      plugins: {
        quadrantBackground: {
          threshold: props.threshold,
        },
        highlightRing: {
          activeId: null,
        },
        tooltip: {
          enabled: false,
          external: externalTooltip,
        },
        legend: { display: false },
      },
      onClick(_event, elements) {
        if (elements.length > 0) {
          const idx = elements[0].index
          const point = data[idx]
          if (point) {
            emit('pointClick', point.id)
          }
        } else {
          emit('chartClick')
        }
      },
      onHover(_event, elements) {
        const canvas = canvasRef.value
        if (canvas) {
          canvas.style.cursor = elements.length > 0 ? 'pointer' : 'default'
        }
      },
    },
    plugins: [quadrantPlugin, clusterPlugin, highlightRingPlugin],
  })

  // Remove load animation after it's done so hover doesn't re-trigger from: 0
  const resetDelay = (data.length - 1) * 18 + 350 + 50
  animResetTimeout = setTimeout(() => {
    animResetTimeout = null
    if (chartInstance) {
      delete chartInstance.options.animations.radius
    }
  }, resetDelay)
}

// --- Highlight a specific point by id (canvas-only, no layout shift) ---
function updateHighlight(id) {
  if (!chartInstance) return
  chartInstance.options.plugins.highlightRing.activeId = id
  chartInstance.draw()
}

watch(
  () => props.highlightedId,
  (id) => updateHighlight(id),
)

watch(
  () => [props.review, props.threshold, props.jitter, props.seed],
  () => {
    nextTick(() => {
      buildChart()
      if (props.highlightedId != null) {
        updateHighlight(props.highlightedId)
      }
    })
  },
  { deep: true },
)

onMounted(() =>
  nextTick(() => {
    buildChart()
    if (canvasRef.value?.parentElement) {
      resizeObserver = new ResizeObserver((entries) => {
        const w = entries[0]?.contentRect?.width
        if (w && Math.abs(w - containerWidth.value) > 2) {
          containerWidth.value = w
          nextTick(buildChart)
        }
      })
      resizeObserver.observe(canvasRef.value.parentElement)
    }
  }),
)

onBeforeUnmount(() => {
  if (animResetTimeout) {
    clearTimeout(animResetTimeout)
    animResetTimeout = null
  }
  if (quadrantAnimFrame) {
    cancelAnimationFrame(quadrantAnimFrame)
    quadrantAnimFrame = null
  }
  resizeObserver?.disconnect()
  if (chartInstance) {
    chartInstance.destroy()
    chartInstance = null
  }
})
</script>

<template>
  <div class="AssessmentChart" aria-hidden="true">
    <div class="AssessmentChart-container">
      <canvas ref="canvasRef" />
      <div ref="tooltipRef" class="AssessmentChart-tooltip" />
    </div>
  </div>
</template>

<style scoped>
.AssessmentChart {
  margin-block: 0;
}

.AssessmentChart-container {
  position: relative;
  width: 100%;
  max-width: 100%;
  display: flex;
  justify-content: center;

  @media screen and (min-width: 1200px) {
    max-height: 75vh;
    width: auto;
    margin-left: auto;
    margin-right: auto;
  }
}

.AssessmentChart-legend {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin-top: 0.75rem;
  font-size: 0.85rem;
  color: #444;
}

.AssessmentChart-legendItem {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
}

.AssessmentChart-legendDot {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
}

.AssessmentChart-tooltip {
  position: absolute;
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.15s ease;
  width: min(300px, 45vw);
  background: #fff;
  color: var(--color-default);
  font-family: Frutiger, Arial, sans-serif;
  font-size: calc(0.85 * var(--fontSize--default));
  line-height: 1.4;
  padding: var(--fontSize--default);
  border-radius: 8px;
  box-shadow:
    rgba(255, 255, 255, 0.1) 0px 1px 1px 0px inset,
    rgba(50, 50, 93, 0.25) 0px 50px 100px -20px,
    rgba(0, 0, 0, 0.3) 0px 30px 60px -30px;
  word-break: break-word;
  white-space: normal;
  text-wrap: balance;

  @media screen and (max-width: 500px) {
    display: none;
  }
}

.AssessmentChart-tooltip :deep(.AssessmentChart-tooltipMeta) {
  display: flex;
  gap: 0.75em;
  margin-top: 0.4em;
  font-size: calc(0.85 * var(--fontSize--default));
  color: var(--color-subtle);
}

.AssessmentChart-tooltip :deep(.AssessmentChart-tooltipKey) {
  font-weight: 700 !important;
  font-family: Frutiger, Arial, sans-serif;
}

.AssessmentChart-tooltip[data-placement='top-left'] {
  transform: translate(calc(-100% - 12px), calc(-100% - 12px));
}

.AssessmentChart-tooltip[data-placement='top-right'] {
  transform: translate(12px, calc(-100% - 12px));
}

.AssessmentChart-tooltip[data-placement='bottom-left'] {
  transform: translate(calc(-100% - 12px), 12px);
}

.AssessmentChart-tooltip[data-placement='bottom-right'] {
  transform: translate(12px, 12px);
}
</style>
